import React, { useState } from 'react';
import { MessageCircle, X, Send, CheckCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AskQuestionButton() {
  const [open, setOpen] = useState(false);
  const { user, token } = useAuth();
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleOpen = () => {
    setOpen(true);
    setSubmitted(false);
    setForm({ name: user?.full_name || '', email: '', message: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      await fetch(`${API}/api/messages`, {
        method: 'POST',
        headers,
        body: JSON.stringify(form),
      });
    } catch {}
    setSubmitted(true);
    setLoading(false);
  };

  return (
    <>
      <button
        onClick={handleOpen}
        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-[#1A2F4A] hover:bg-[#243a58] text-white pl-4 pr-5 py-3 rounded-full shadow-lg text-sm font-semibold transition-all flex items-center gap-2"
        data-testid="ask-question-btn">
        <MessageCircle className="w-4 h-4" />
        Poser une question
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-7" data-testid="ask-question-modal">
            <button
              onClick={() => setOpen(false)}
              className="absolute top-4 right-4 text-[#999] hover:text-[#1A2F4A] transition-colors"
              data-testid="ask-question-close">
              <X className="w-5 h-5" />
            </button>

            {submitted ? (
              <div className="text-center py-6" data-testid="ask-question-success">
                <CheckCircle className="w-10 h-10 text-[#D4AF37] mx-auto mb-3" />
                <p className="font-semibold text-[#1A2F4A] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Merci
                </p>
                <p className="text-sm text-[#6B4E37]">
                  Votre message a été envoyé. Nous vous répondrons sous 24 heures.
                </p>
              </div>
            ) : (
              <>
                <h3 className="text-lg font-bold text-[#1A2F4A] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Poser une question
                </h3>
                <p className="text-xs text-[#999] mb-5">
                  Have a question? You can send it directly — we'll be happy to help.
                </p>
                <form onSubmit={handleSubmit} className="space-y-3.5">
                  {!user && (
                    <>
                      <input
                        type="text"
                        value={form.name}
                        onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Your name (optional)"
                        className="w-full px-4 py-2.5 border border-[#E8E4DC] rounded-xl text-sm text-[#1A2F4A] bg-[#FAFAF8] focus:outline-none focus:border-[#D4AF37]"
                        data-testid="ask-question-name"
                      />
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="Your email"
                        required
                        className="w-full px-4 py-2.5 border border-[#E8E4DC] rounded-xl text-sm text-[#1A2F4A] bg-[#FAFAF8] focus:outline-none focus:border-[#D4AF37]"
                        data-testid="ask-question-email"
                      />
                    </>
                  )}
                  <textarea
                    value={form.message}
                    onChange={(e) => setForm(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Write your message here..."
                    required
                    rows={4}
                    className="w-full px-4 py-2.5 border border-[#E8E4DC] rounded-xl text-sm text-[#1A2F4A] bg-[#FAFAF8] focus:outline-none focus:border-[#D4AF37] resize-none"
                    data-testid="ask-question-message"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#1A2F4A] hover:bg-[#243a58] text-white py-3 rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                    data-testid="ask-question-submit">
                    {loading ? 'Envoi...' : 'Envoyer'}
                    {!loading && <Send className="w-3.5 h-3.5" />}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
